/* eslint-disable complexity */
import React, { useContext, useEffect, useState } from 'react';
import clsx from 'clsx';
import { makeStyles } from 'design-system/styles';
import { CircularProgress } from 'design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { NewDatePicker } from 'design-system/DatePicker';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import Link from 'corp/components/UI/IsomorphicLink/IsomorphicLink';
import Dialog, { DialogContent, DialogActions } from 'react-scale/components/design-system/Dialog';
import { CorpRoleView, DEFAULT_DIFF_BETWEEN_ROLE_START_AND_END_DATE } from '@scale/shared/defaults';
import { IProjectRole, Role, RoleDescription } from '@scale/shared/corp/projectRoles';
import { Button } from 'design-system/Button';
import { IProjectJson } from '@scale/shared/types/project';
import Tooltip from 'design-system/Tooltip';
import { getUserName } from 'corp/components/CorpRoles/helper';
import { ProjectRoleModifyRequest } from '@scale/shared/qm/qmRequestTypes';
import ProjectRolesResultsPopup, {
  ProjectRoleResult,
} from 'react-scale/corp/components/CorpRoles/ProjectRolesResultsPopup';
import RemoAdminContext from 'react-scale/components/remoAdmin/remoAdminContext';

interface IProjectLabelProps {
  user: any;
  role: IProjectRole;
  title: string | undefined;
  view: CorpRoleView;
  roleText?: Role;
  buttonLabels?: (string | undefined)[];
  buttonActions?: (((projectRole: IProjectRole) => void) | undefined)[];
  checkboxSelected?: boolean;
  checkboxAction?: () => void;
  showLoadingView?: boolean;
}

const WorkCard = ({
  user,
  role,
  title,
  view,
  roleText,
  buttonLabels,
  buttonActions,
  checkboxSelected,
  checkboxAction,
  showLoadingView,
}: IProjectLabelProps) => {
  const style = useStyles();
  const remoAdminCtx = useContext(RemoAdminContext);
  const momentFormat = 'MMMM DD, YYYY';
  const project = role.project as IProjectJson;

  const WorkCardCheckbox = ({
    isSelected,
    action,
  }: {
    isSelected?: boolean;
    action: () => void;
  }) => {
    return (
      <i
        className={`${style.workCardCheckbox} ${isSelected ? 'selected' : ''}`}
        onClick={() => {
          action();
        }}
      >
        {isSelected && <FontAwesomeIcon icon="check" style={{ color: 'white' }} />}
      </i>
    );
  };

  return (
    <div className={style.workCard}>
      {checkboxAction && <WorkCardCheckbox isSelected={checkboxSelected} action={checkboxAction} />}
      {showLoadingView && (
        <div className={style.genericWorkCardLoadingView}>
          <CircularProgress size="24px" color="inherit" />
        </div>
      )}
      <div
        className={clsx(style.cardDescription, {
          'with-checkbox': checkboxAction,
        })}
      >
        <div>
          {view === CorpRoleView.Role && roleText !== undefined && (
            <Tooltip
              className={style.roleCard}
              title={RoleDescription[roleText].helpText}
              placement="top"
            >
              <a>{title}</a>
            </Tooltip>
          )}
          {view === CorpRoleView.Role && !roleText && <div className={style.roleCard}>{title}</div>}
          {view === CorpRoleView.Project &&
            (remoAdminCtx.isRemoAdmin ? (
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: 13,
                }}
              >
                {title}
              </span>
            ) : (
              <Link
                style={{
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: 13,
                }}
                key={title}
                href={`/lookup/${project._id}`}
              >
                {title}
              </Link>
            ))}
          {view === CorpRoleView.Scalien &&
            (remoAdminCtx.isRemoAdmin ? (
              <span
                style={{
                  fontWeight: 'bold',
                  fontSize: 13,
                }}
              >
                {title}
              </span>
            ) : (
              <Link
                style={{
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  fontSize: 13,
                }}
                key={title}
                href={`/lookup/${user._id}`}
              >
                {title}
              </Link>
            ))}
        </div>

        <div>
          {role.stopDate ? 'From ' : 'Since '}
          {moment(role.startDate).format(momentFormat)}{' '}
          {role.stopDate ? `to ${moment(role.stopDate).format(momentFormat)}` : ''}
        </div>
      </div>
      {buttonActions?.length && buttonLabels?.length && !showLoadingView && (
        <div className={style.buttonLabelContainer} style={{ display: 'flex', height: '1' }}>
          {buttonActions.map((action, index) => {
            const labelForThisAction = buttonLabels[index];
            if (!action || !labelForThisAction) {
              return null;
            }

            return (
              <div
                key={`workCardActionButton-${user.id}-${role.id}`}
                className={style.buttonLabel}
                onClick={() => action(role)}
              >
                {' '}
                {labelForThisAction}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const modifyRoles = async (
  projectRolesToModify: ProjectRoleModifyRequest,
  counter: number,
  setCounter: (x: number) => void,
  setProjectRoleOperationsResults: (x: ProjectRoleResult[]) => void,
) => {
  if (projectRolesToModify.length > 0) {
    try {
      const modifyRolesResults = await apiFetch<(IProjectRole | Error)[]>(
        null,
        `corp-api/qm/project_role_events/`,
        {
          method: 'PATCH',
          body: projectRolesToModify,
        },
      );
      setProjectRoleOperationsResults(
        modifyRolesResults.map(result => ({
          error: !!(result as Error).message,
          errorMessage: (result as Error).message || '',
          result,
        })),
      );

      setCounter(counter + 1);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
  }
};

interface IModifyLabelProps {
  user: any;
  role: IProjectRole;
  title: string | undefined;
  view: CorpRoleView;
  roleText?: Role;
  counter: number;
  setCounter: (n: number) => void;
}

export const ModifyWorkCard = ({
  user,
  role,
  title,
  view,
  roleText,
  counter,
  setCounter,
}: IModifyLabelProps) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const momentFormat = 'MMMM DD, YYYY';
  const project = role.project as IProjectJson;
  const userName = getUserName(user);
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(new Date(role.startDate));
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(
    role.stopDate
      ? new Date(role.stopDate)
      : new Date(Date.now() + DEFAULT_DIFF_BETWEEN_ROLE_START_AND_END_DATE),
  );
  const [endDateIsLowerThanStartDate, setEndDateIsLowerThanStartDate] = useState<boolean>(false);
  const [isModifying, setIsModifying] = useState<boolean>(false);
  const [projectRoleOperationsResults, setProjectRoleOperationsResults] = useState<
    ProjectRoleResult[]
  >([]);
  const [showResultsPopup, setShowResultsPopup] = useState<boolean>(false);
  const style = useStyles();

  useEffect(() => {
    const getMidnightHour = (date: Date): Date => {
      const newDate = date;
      newDate.setHours(0);
      newDate.setMinutes(0);
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      return newDate;
    };

    setEndDateIsLowerThanStartDate(
      selectedStartDate && selectedEndDate
        ? getMidnightHour(selectedEndDate) <= getMidnightHour(selectedStartDate)
        : false,
    );
  }, [selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (projectRoleOperationsResults.length > 0) {
      setShowResultsPopup(true);
    }
  }, [projectRoleOperationsResults]);

  return (
    <div
      className={`${style.workCard} ${role.pendingToAdd && style.pendingToAdd} ${
        isModifying && style.isModifying
      }`}
    >
      {isModifying && (
        <div className={style.isModifyingLoadingView}>
          <CircularProgress size="24px" color="inherit" />
        </div>
      )}
      {role.pendingToAdd && <div className={style.infoUpperMessage}>Creation pending approval</div>}
      {role.pendingToModify && (
        <div className={style.infoUpperMessage}>Changes pending approval</div>
      )}
      <div className={style.cardDescription}>
        <div className={style.infoTooltip}>
          {role.pendingToAdd && (
            <Tooltip
              className={style.roleCard}
              title="This role has pending approval"
              placement="top"
            >
              <a>
                <FontAwesomeIcon
                  icon="exclamation-circle"
                  style={{ cursor: 'pointer' }}
                  color="gray"
                />
              </a>
            </Tooltip>
          )}
          {role.pendingToModify && (
            <Tooltip
              className={style.roleCard}
              title={`${
                role.newStartDate
                  ? `Pending start date: ${moment(role.newStartDate).format(momentFormat)}. `
                  : ''
              } ${
                role.newStopDate
                  ? `Pending end date: ${moment(role.newStopDate).format(momentFormat)}.`
                  : ''
              }`}
              placement="top"
            >
              <a>
                <FontAwesomeIcon
                  icon="exclamation-circle"
                  style={{ cursor: 'pointer' }}
                  color="gray"
                />
              </a>
            </Tooltip>
          )}
        </div>
        <div>
          {view === CorpRoleView.Role && roleText !== undefined && (
            <Tooltip
              className={style.roleCard}
              title={RoleDescription[roleText].helpText}
              placement="top"
            >
              <a>{title}</a>
            </Tooltip>
          )}
          {view === CorpRoleView.Role && !roleText && <div className={style.roleCard}>{title}</div>}
          {view === CorpRoleView.Project && (
            <Link
              style={{
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: 13,
              }}
              key={title}
              href={`/lookup/${project._id}`}
            >
              {title}
            </Link>
          )}
          {view === CorpRoleView.Scalien && (
            <Link
              style={{
                textDecoration: 'none',
                fontWeight: 'bold',
                fontSize: 13,
              }}
              key={title}
              href={`/lookup/${user._id}`}
            >
              {title}
            </Link>
          )}
        </div>

        <div>
          {role.stopDate ? 'From ' : 'Since '}
          {moment(role.startDate).format(momentFormat)}{' '}
          {role.stopDate ? `to ${moment(role.stopDate).format(momentFormat)}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', height: '1' }}>
        {!role.pendingToAdd && (
          <div className={style.buttonLabel} onClick={() => setShowDialog(true)}>
            {' '}
            Modify Role
          </div>
        )}
        <Dialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          style={{ overflow: 'visible' }}
        >
          <DialogContent className={style.dialogContent}>
            <div className={style.title}>
              Modify Role ({role.role}) for {userName}
            </div>
            <div style={{ padding: 5 }}>
              <NewDatePicker
                calendarClassName={style.roleCalendar}
                placeholderText={'Select Start Date'}
                isClearable={true}
                dateFormat={'MMMM dd, yyyy'}
                selected={selectedStartDate}
                wrapperClassName={style.roleCalendar}
                onChange={(time: any) => {
                  setSelectedStartDate(time);
                }}
              />
            </div>
            <div style={{ padding: 5 }}>
              <NewDatePicker
                errors={[
                  {
                    verifier: endDateIsLowerThanStartDate,
                    message: 'The end date cannot be lower or equal to the start date.',
                  },
                ]}
                calendarClassName={style.roleCalendar}
                placeholderText={'Select End Date'}
                isClearable={true}
                dateFormat={'MMMM dd, yyyy'}
                selected={selectedEndDate}
                wrapperClassName={style.roleCalendar}
                onChange={(time: any) => {
                  setSelectedEndDate(time);
                }}
              />
            </div>
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center' }}>
            <Button onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button
              type="primary"
              disabled={endDateIsLowerThanStartDate}
              onClick={async () => {
                setIsModifying(true);
                setShowDialog(false);
                setSelectedStartDate(selectedStartDate);
                setSelectedEndDate(selectedEndDate);

                const projectRolesToModify: ProjectRoleModifyRequest = [];

                projectRolesToModify.push({
                  projectRoleId: role._id!,
                  newStartDate: selectedStartDate,
                  newEndDate: selectedEndDate,
                });

                await modifyRoles(
                  projectRolesToModify,
                  counter,
                  setCounter,
                  setProjectRoleOperationsResults,
                );

                setIsModifying(false);
              }}
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        <style jsx global>{`
          .MuiPaper-root {
            overflow: visible !important;
          }
        `}</style>
      </div>
      <ProjectRolesResultsPopup
        isModalOpen={showResultsPopup}
        hideModal={() => setShowResultsPopup(false)}
        title="Modify Roles: Results"
        results={projectRoleOperationsResults}
      />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  isModifying: {
    position: 'relative',
    opacity: '0.5',
  },
  isModifyingLoadingView: {
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'black',
  },
  genericWorkCardLoadingView: {
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
    color: 'black',
    background: 'rgba(255,255,255,0.5)',
  },
  buttonLabelContainer: {
    position: 'absolute',
    right: '6px',
    bottom: '-5px',
    display: 'flex',
    flex: '1',
  },
  buttonLabel: {
    fontSize: '9px',
    alignItems: 'center',
    paddingLeft: '5px',
    paddingRight: '5px',
    borderRadius: 4,
    cursor: 'pointer',
    background: 'white',
    color: 'var(--color-RemoGray50)',
    '&:hover': {
      backgroundColor: '#F2F2F2',
      color: '#8B0000',
      borderRadius: 4,
    },
  },
  roleText: {
    paddingLeft: 5,
    '&:hover': {
      borderRadius: '50%',
    },
  },
  workCard: {
    margin: 5,
    border: 'var(--color-RemoGray20) solid 1px',
    background: 'var(--color-RemoGray00)',
    borderRadius: 4,
    position: 'relative',
  },
  workCardCheckbox: {
    width: '16px',
    height: '16px',
    border: '1px solid rgb(212 212 212)',
    borderRadius: '2px',
    padding: '2px',
    position: 'absolute',
    top: '4px',
    right: '4px',
    zIndex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgb(250 250 250)',
    cursor: 'pointer',
    '&.selected': {
      borderColor: 'rgb(43 153 216)',
      backgroundColor: 'rgb(43 153 216)',
    },
  },
  infoUpperMessage: {
    backgroundColor: 'var(--color-RemoBlue)',
    color: 'white',
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 600,
    lineHeight: '1',
    padding: '4px 7px',
    fontSize: '10px',
  },
  infoTooltip: {
    position: 'absolute',
    top: '2px',
    right: '5px',
  },
  pendingToAdd: {
    opacity: '0.5',
  },
  cardDescription: {
    position: 'relative',
    fontSize: 8,
    color: 'black',
    fontWeight: 'lighter',
    padding: 10,
    '&.with-checkbox': {
      paddingRight: 26,
    },
  },
  selectRoles: {
    fontSize: 8,
    color: 'black',
    fontWeight: 'lighter',
    padding: 10,
    width: '225px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  addCard: {
    alignContent: 'center',
    fontSize: 15,
    margin: 5,
    color: '#169016',
    fontWeight: 'lighter',
    border: 'var(--color-RemoGray20) dashed 1px',
    borderRadius: 4,
    padding: '0px 5px 0px 5px',
    '&:hover': {
      backgroundColor: '#169016',
      color: 'white',
      cursor: 'pointer',
    },
  },
  roleCalendar: {
    display: 'flex',
    flexDirection: 'column',
    '& input': {
      width: '100%',
      padding: '10px',
      border: '#ccc solid 1px',
      borderRadius: '4px',
    },
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    padding: '5px',
  },
  dialogContent: {
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    width: '500px',
  },
  roleCard: {
    textDecoration: 'none',
    color: 'var(--color-RemoBlue)',
    fontWeight: 'bold',
    fontSize: 13,
  },
}));

export default WorkCard;
